import api from "./axios";
import type { DocumentEmailResult } from "./invoice.api";

// M13 — internal company-commission fields on a ticket line. Optional; when
// filled, all three are required (validated server-side). Never rendered on
// customer PDFs.
export interface CompanyCommissionInput {
  enabled?: boolean; // client-side form toggle; stripped before API submit
  supplierName: string;
  priceHt: number;
  commissionPct: number;
}

export interface CompanyCommissionView {
  supplierId: number;
  supplierName: string;
  priceHt: string;
  commissionPct: string;
  estimatedAmount: string;
}

export interface TicketDetailsInput {
  travelClass: "economy" | "business" | "first" | "premium";
  passengerName: string;
  segments: Array<{
    from: string;
    to: string;
    date: string;
    returnDate?: string;
    flightNo?: string;
    tripType?: "one_way" | "round_trip";
  }>;
  references?: { pnr: string; gds: string; ticketNumber?: string };
  supplierPrice: number;
  sellingPrice: number;
  segment: string; // internal ticket-line categorization; mandatory, never on PDFs
  companyCommission?: CompanyCommissionInput;
}

export interface ShopDetailsInput {
  articleId?: number;
  supplierPrice: number;
  sellingPrice: number;
  passengerName?: string;
}

export interface DocumentLineInput {
  lineType: "ticket" | "shop";
  description: string;
  quantity?: number;
  unitPrice: number;
  discount?: number;
  ticketDetails?: TicketDetailsInput;
  shopDetails?: ShopDetailsInput;
}

export interface TicketDetailsView {
  id: number;
  travelClass: string;
  passengerName: string;
  segments: Array<{
    from: string;
    to: string;
    date: string;
    returnDate?: string;
    flightNo?: string;
  }>;
  references?: { pnr: string; gds: string; ticketNumber?: string };
  supplierPrice: string;
  sellingPrice: string;
  segment?: string; // internal — never on customer PDFs
  companyCommission?: CompanyCommissionView; // internal — never on customer PDFs
}

export interface ShopDetailsView {
  id: number;
  articleId?: number;
  supplierPrice: string;
  sellingPrice: string;
  passengerName?: string;
}

export interface DocumentLineView {
  id: number;
  lineType: string;
  description: string;
  quantity: number;
  unitPrice: string;
  discount: string;
  lineTotal: string;
  ticketDetails?: TicketDetailsView;
  shopDetails?: ShopDetailsView;
}

export interface Proforma {
  id: number;
  number: string;
  partyId: number;
  referrerPartyId?: number;
  partySnapshot: { fullName?: string; phone?: string; email?: string };
  status: "draft" | "issued" | "expired" | "cancelled";
  expiresAt?: string;
  createdBy: number;
  responsibleAgentId?: number;
  createdAt: string;
  lines: DocumentLineView[];
  // Set only when fetched via getById (detail page) — see server comment.
  promotedInvoiceId?: number;
  promotedInvoiceNumber?: string;
}

export const proformaApi = {
  list: (partyId?: number) => api.get<Proforma[]>("/proformas", { params: { partyId } }),
  getById: (id: number) => api.get<Proforma>(`/proformas/${id}`),
  create: (
    partyId: number,
    lines: DocumentLineInput[],
    referrerPartyId?: number,
    responsibleAgentId?: number,
  ) => api.post<Proforma>("/proformas", { partyId, referrerPartyId, lines, responsibleAgentId }),
  reassignResponsibleAgent: (id: number, responsibleAgentId: number, reason?: string) =>
    api.patch<Proforma>(`/proformas/${id}/responsible-agent`, { responsibleAgentId, reason }),
  addLine: (proformaId: number, line: DocumentLineInput) =>
    api.post<Proforma>(`/proformas/${proformaId}/lines`, line),
  updateLine: (proformaId: number, lineId: number, patch: Partial<DocumentLineInput>) =>
    api.patch<Proforma>(`/proformas/${proformaId}/lines/${lineId}`, patch),
  removeLine: (proformaId: number, lineId: number) =>
    api.delete<Proforma>(`/proformas/${proformaId}/lines/${lineId}`),
  sendEmail: (proformaId: number, to?: string) =>
    api.post<DocumentEmailResult>(`/proformas/${proformaId}/email`, { to }, { timeout: 60_000 }),
  remove: (id: number) => api.delete<Proforma>(`/proformas/${id}`),
  restore: (id: number) => api.post<Proforma>(`/proformas/${id}/restore`),
  trash: () => api.get<Proforma[]>("/proformas/trash"),
};
